import { useQuery } from '@tanstack/react-query';
import { useRef, useCallback } from 'react';
import { api } from '@/lib/api-client';
import type { LinkResponse } from '@readlater/shared';

interface UseLinkOptions {
  enabled?: boolean;
}

// Exponential backoff config for polling processing links
const POLL_INITIAL_MS = 2000; // Start at 2 seconds
const POLL_MAX_MS = 30000; // Cap at 30 seconds
const POLL_MULTIPLIER = 1.5; // Increase by 50% each time

export function useLink(id: number | string, options: UseLinkOptions = {}) {
  const { enabled = true } = options;
  const pollIntervalRef = useRef(POLL_INITIAL_MS);

  // Calculate refetch interval based on whether link is processing
  const getRefetchInterval = useCallback((query: { state: { data?: LinkResponse } }) => {
    const data = query.state.data;
    const isProcessing = data?.status === 'pending' || data?.status === 'processing';

    if (!isProcessing) {
      // Reset backoff when not processing
      pollIntervalRef.current = POLL_INITIAL_MS;
      return false;
    }

    // Get current interval and schedule next with backoff
    const currentInterval = pollIntervalRef.current;
    pollIntervalRef.current = Math.min(currentInterval * POLL_MULTIPLIER, POLL_MAX_MS);

    return currentInterval;
  }, []);

  return useQuery({
    queryKey: ['link', id],
    queryFn: () => api.get<LinkResponse>(`/links/${id}`),
    enabled: enabled && !!id,
    staleTime: 60 * 1000, // Consider data fresh for 1 minute
    refetchInterval: getRefetchInterval,
  });
}
